import { useBooleanState } from '@cian/valuation-utils-component';
import * as React from 'react';
import { useDispatch, useSelector, batch } from 'react-redux';
import { HideOfferButton } from 'shared/components/OfferHistory/containers/HideOfferButton';

import { FeedContainer } from './FeedContainer';
import { FiltersContainer } from './FiltersContainer';
import { NearbyHousesInfoContainer } from './NearbyHousesInfoContainer';
import { OfferFromHistoryContainer } from './OfferFromHistoryContainer';
import { ShowMoreContainer } from './ShowMoreContainer';
import { trackFullscreenViewOpen } from './analytics';
import { useHandleOfferRowClick } from './utils/useHandleOfferRowClick';
import { useTitle } from './utils/useTitle';
import {
  getCheckAdminAccess,
  getOffersHistory,
  resetFilters,
  resetListingData,
  setActiveOfferId,
  setViewedOffer,
} from '../../../actions/offersHistory';
import { selectActiveOfferId, selectOffersHistoryData } from '../../../selectors/offersHistory';
import { FullscreenView } from '../components';

interface IFullscreenViewContainerProps {
  fullscreenOpenState: ReturnType<typeof useBooleanState>;
  onClose(): void;
}

export const FullscreenViewContainer: React.FC<IFullscreenViewContainerProps> = props => {
  const { fullscreenOpenState, onClose } = props;
  const dispatch = useDispatch();
  const [isFullscreenViewOpened, openFullscreenView, closeFullscreenView] = fullscreenOpenState;
  const handleOfferClick = useHandleOfferRowClick(openFullscreenView);
  const activeOfferId = useSelector(selectActiveOfferId);
  const { data: offersHistoryData, isFetching } = useSelector(selectOffersHistoryData);
  const title = useTitle();

  const onFullscreenClose = React.useCallback(() => {
    closeFullscreenView();

    /** При закрытии фулскрина, фильтры, которые накликал юзер и данные листинга сбросятся */
    batch(() => {
      dispatch(setActiveOfferId(null));
      dispatch(resetListingData());
      dispatch(resetFilters());
      onClose();
    });
  }, [closeFullscreenView, dispatch, onClose]);

  /**
   * Если в открытом фулскрине нет данных листинга и они не в процессе загрузки,
   * то загрузить их и открыть оффер в сайдбаре
   * */
  React.useEffect(() => {
    if (isFullscreenViewOpened && !offersHistoryData && !isFetching) {
      dispatch(getOffersHistory());
      dispatch(getCheckAdminAccess());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFullscreenViewOpened]);

  /** Отмечает оффер как просмотренный */
  React.useEffect(() => {
    if (isFullscreenViewOpened && activeOfferId) {
      dispatch(setViewedOffer(activeOfferId));
    }
  }, [isFullscreenViewOpened, activeOfferId, dispatch]);

  return (
    <FullscreenView
      title={title}
      isOpened={isFullscreenViewOpened}
      onClose={onFullscreenClose}
      offersTable={
        <FeedContainer
          onOfferClick={handleOfferClick}
          showMoreBtn={<ShowMoreContainer />}
          activeOfferId={activeOfferId}
          scrollable
          selectable
          hideButton={<HideOfferButton />}
        />
      }
      filters={<FiltersContainer />}
      nearbyHousesInfo={<NearbyHousesInfoContainer />}
      offerFromHistory={<OfferFromHistoryContainer key={activeOfferId} />}
      onView={trackFullscreenViewOpen}
    />
  );
};
